<template>
  <section class="rf-descarga-lote d-print-none" aria-label="Descarga de reportes">
    <slot name="barra" :disponible="disponible" :ocupado="ocupado" :iniciar="iniciar">
      <custom-button tipo="agregar" iconText="file_download"
        label="Descargar todos los reportes" :disabled="!disponible || ocupado"
        :loading="ocupado" @click="iniciar" />
    </slot>
    <div v-if="archivos.length" class="rf-descarga-estado">
      <div class="rf-descarga-cabecera">
        <v-icon color="secondary" aria-hidden="true">
          {{ ocupado ? 'description'
            : pendientes.length ? 'info_outline' : 'check_circle_outline' }}
        </v-icon>
        <h2>{{ ocupado ? 'Preparando tus reportes' : pendientes.length
          ? 'Descarga pendiente de completar' : 'Reportes listos para descargar' }}</h2>
        <span class="rf-descarga-contador">{{ listos.length }} / {{ archivos.length }} PDF</span>
      </div>
      <div role="status" aria-live="polite" aria-atomic="true">
        <p v-if="generando && actual">Generando reporte {{ actual.reporte.numero }}…</p>
        <p v-else-if="creandoZip">Empaquetando los PDF en un archivo ZIP…</p>
        <p v-else-if="cancelado">Generación cancelada. Los PDF terminados están disponibles.</p>
        <p v-else-if="fallidos.length">
          {{ listos.length }} de {{ archivos.length }} PDF disponibles.
          {{ fallidos.length }}
          {{ fallidos.length === 1
            ? 'reporte no pudo generarse.' : 'reportes no pudieron generarse.' }}
        </p>
        <p v-else-if="error">Los PDF están listos. Vuelve a intentar la descarga del ZIP.</p>
        <p v-else>Descarga enviada al navegador. Puedes descargar el archivo nuevamente.</p>
      </div>
      <v-progress-linear v-if="ocupado" :value="progreso"
        :indeterminate="creandoZip" color="secondary"
        rounded height="8" aria-label="Progreso de generación de reportes"
        :aria-valuetext="`${terminados} de ${archivos.length} reportes procesados`" />
      <p v-if="fallidos.length" class="rf-descarga-detalle">
        Reportes pendientes de reintento: {{ numerosFallidos }}.
        Puedes reintentar o descargar los PDF disponibles.
      </p>
      <div class="rf-descarga-opciones">
        <v-btn v-if="generando" outlined color="primary" @click="cancelar">Cancelar</v-btn>
        <v-btn v-if="pendientes.length && !generando" text color="primary"
          :disabled="ocupado || !disponible" @click="generar(pendientes)">
          {{ cancelado ? 'Continuar pendientes' : 'Reintentar fallidos' }}
        </v-btn>
        <v-btn v-if="listos.length && !generando" text color="primary"
          :disabled="ocupado" @click="descargarZip">
          Descargar ZIP {{ pendientes.length ? 'parcial' : '' }} ({{ listos.length }} PDF)
        </v-btn>
      </div>
    </div>
    <p v-if="error" class="rf-descarga-error" role="alert">{{ error }}</p>
  </section>
</template>

<script>
import axios from 'axios';
import fileDownload from 'js-file-download';
import CustomButton from '@/components/buttons/Button.vue';
import { crearDocumentoReporteFisico } from './reporteFisico';
import { generarPdfReporte, crearZipReportes, nombreArchivoReporte } from './exportarPdf';

export default {
  name: 'DescargarReportesFisicos',
  components: { CustomButton },
  props: {
    reportes: { type: Array, default: () => [] },
    contrato: { type: Object, default: null },
    catalogoConceptos: { type: Array, default: () => [] },
    cargando: { type: Boolean, default: false },
  },
  data: () => ({
    archivos: [],
    generando: false,
    creandoZip: false,
    cancelado: false,
    actual: null,
    cancelacion: null,
    error: '',
    contratoLote: null,
    catalogoLote: [],
    abandonado: false,
  }),
  computed: {
    disponible() { return !!this.contrato && !!this.reportes.length && !this.cargando; },
    ocupado() { return this.generando || this.creandoZip; },
    listos() { return this.archivos.filter((item) => item.estado === 'listo'); },
    fallidos() { return this.archivos.filter((item) => item.estado === 'error'); },
    pendientes() { return this.archivos.filter((item) => item.estado !== 'listo'); },
    terminados() { return this.listos.length + this.fallidos.length; },
    progreso() {
      return this.archivos.length ? (this.terminados / this.archivos.length) * 100 : 0;
    },
    numerosFallidos() { return this.fallidos.map((item) => item.reporte.numero).join(', '); },
  },
  beforeDestroy() {
    this.abandonado = true;
    this.cancelar();
  },
  methods: {
    async iniciar() {
      if (!this.disponible || this.ocupado) return;
      this.contratoLote = JSON.parse(JSON.stringify(this.contrato));
      this.catalogoLote = JSON.parse(JSON.stringify(this.catalogoConceptos));
      this.archivos = JSON.parse(JSON.stringify(this.reportes)).map((reporte) => ({
        reporte, estado: 'pendiente', pdf: null,
      }));
      await this.generar(this.archivos);
    },
    async generar(pendientes) {
      if (this.ocupado || !this.disponible) return;
      this.generando = true;
      this.cancelado = false;
      this.error = '';
      // Reintentar solo los pendientes conserva los PDF ya generados.
      pendientes.forEach((item) => {
        const archivo = item;
        archivo.estado = 'pendiente';
      });
      try {
        // eslint-disable-next-line no-restricted-syntax
        for (const item of pendientes) {
          if (this.cancelado || this.abandonado) break;
          this.actual = item;
          item.estado = 'generando';
          this.cancelacion = axios.CancelToken.source();
          try {
            if (String(item.reporte.idContrato) !== String(this.contratoLote.id)) {
              throw new Error('El reporte no pertenece al contrato.');
            }
            const documento = crearDocumentoReporteFisico({
              contrato: this.contratoLote,
              reporte: item.reporte,
              encabezado: this.mixGeneraEncabezadosPdf(item.reporte.fecha, []),
              colores: this.$vuetify.theme.themes.light,
              servidorArchivos: this.$urlFileServer,
              catalogoConceptos: this.catalogoLote,
            });
            // Una sola solicitud activa por lote limita la carga del generador.
            // eslint-disable-next-line no-await-in-loop
            const pdf = await generarPdfReporte(this.axios, documento, this.cancelacion.token);
            if (this.cancelado || this.abandonado) { item.estado = 'pendiente'; break; }
            item.pdf = pdf;
            item.estado = 'listo';
          } catch (error) {
            item.estado = this.cancelado || axios.isCancel(error) ? 'pendiente' : 'error';
          }
        }
      } finally {
        this.actual = null;
        this.cancelacion = null;
        this.generando = false;
      }
      if (!this.cancelado && !this.abandonado && !this.pendientes.length) await this.descargarZip();
    },
    cancelar() {
      this.cancelado = true;
      if (this.cancelacion) this.cancelacion.cancel('Generación cancelada');
    },
    async descargarZip() {
      if (this.ocupado || !this.listos.length || this.abandonado) return;
      this.creandoZip = true;
      this.error = '';
      try {
        const archivos = this.listos.map((item) => ({
          // El ID evita colisiones incluso si existen números de reporte repetidos.
          nombre: `${nombreArchivoReporte(this.contratoLote, item.reporte)}_${String(item.reporte.id).replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`,
          pdf: item.pdf,
        }));
        const zip = await crearZipReportes(archivos);
        if (this.abandonado) return;
        const contrato = String(this.contratoLote.numero || 'Contrato').replace(/[^a-zA-Z0-9_.-]/g, '_');
        fileDownload(zip, `${contrato}_Reportes_fisicos${this.pendientes.length ? '_parcial' : ''}.zip`);
      } catch (error) {
        this.error = 'No se pudo preparar el ZIP. Tus PDF siguen disponibles; vuelve a intentar la descarga.';
      } finally {
        this.creandoZip = false;
      }
    },
  },
};
</script>

<style scoped>
.rf-descarga-lote { margin-bottom: 20px; color: var(--v-primary-base); }
.rf-descarga-estado { padding: 20px 24px; background: var(--v-base5-base); border-radius: 12px; }
.rf-descarga-cabecera { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.rf-descarga-cabecera h2 { font-size: 16px; line-height: 1.5; font-weight: 600; margin: 0; }
.rf-descarga-contador { margin-left: auto; white-space: nowrap; font-size: 14px;
  font-weight: 600; font-variant-numeric: tabular-nums; }
.rf-descarga-estado p { margin: 0 0 12px; font-size: 14px; line-height: 1.6; }
.rf-descarga-detalle { margin-top: 16px !important; }
.rf-descarga-opciones { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px; }
.rf-descarga-opciones .v-btn { min-height: 44px; }
.rf-descarga-error { margin: 12px 0 0; font-size: 14px; }
@media (max-width: 600px) {
  .rf-descarga-estado { padding: 16px; }
  .rf-descarga-cabecera { flex-wrap: wrap; gap: 8px; }
  .rf-descarga-cabecera h2 { flex: 1; min-width: 180px; }
  .rf-descarga-contador { margin-left: 32px; }
  .rf-descarga-opciones { flex-direction: column; align-items: stretch; }
  .rf-descarga-opciones .v-btn {
    white-space: normal; height: auto; padding: 12px; font-size: 12px;
  }
  .rf-descarga-opciones ::v-deep .v-btn__content {
    white-space: normal; line-height: 1.5; max-width: 100%;
  }
}
</style>
